import { Link } from "react-router-dom";
import { BookOpen, ArrowRight, Calendar, Clock } from "lucide-react";
import { getRelatedInsightsForTool } from "@/data/toolInsightLinks";
import { getInsightBySlug } from "@/data/insights";
import { analytics } from "@/hooks/useAnalytics";

interface RelatedInsightsProps {
  toolId?: string;
  slugs?: string[];
  limit?: number;
}

export const RelatedInsights = ({ toolId, slugs, limit = 3 }: RelatedInsightsProps) => {
  const relatedSlugs = slugs ?? (toolId ? getRelatedInsightsForTool(toolId) : []);
  const insights = relatedSlugs
    .slice(0, limit)
    .map(slug => getInsightBySlug(slug))
    .filter(Boolean) as NonNullable<ReturnType<typeof getInsightBySlug>>[];

  if (insights.length === 0) return null;

  const handleInsightClick = (slug: string, title: string) => {
    analytics.ctaClicked(`related_insight_${slug}`, `/insights/${slug}`);
  };

  return (
    <section className="mt-10 pt-8 border-t border-border">
      <div className="flex items-center gap-2 mb-6">
        <BookOpen className="w-5 h-5 text-primary" />
        <h2 className="font-display text-xl font-semibold text-foreground">
          Learn More
        </h2>
      </div>
      <p className="text-muted-foreground text-sm mb-6">
        Dive deeper into the concepts behind this tool with our research insights:
      </p>
      
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {insights.map((insight) => (
          <Link
            key={insight.slug}
            to={`/insights/${insight.slug}`}
            onClick={() => handleInsightClick(insight.slug, insight.title)}
            className="group p-5 rounded-xl bg-card border border-border hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all"
          >
            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary mb-3">
              {insight.tag}
            </span>
            
            <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2 line-clamp-2">
              {insight.title}
            </h3>
            
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {insight.subtitle}
            </p>
            
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {insight.readTime}
              </span>
            </div>
            
            <div className="flex items-center gap-1 mt-3 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
              Read article
              <ArrowRight className="w-3 h-3" />
            </div>
          </Link>
        ))}
      </div>
      
      <Link
        to="/"
        className="inline-flex items-center gap-1 mt-6 text-sm text-primary hover:text-primary/80 transition-colors"
      >
        Browse all insights
        <ArrowRight className="w-4 h-4" />
      </Link>
    </section>
  );
};
